import { memo } from 'react';

import { EventMykissCtaButtonClicked } from '@generated/events';
import { CatchLocationPrivacyTypes } from '../../generated/graphql';

import { CatchMapBody } from './components/CatchMapBody';
import { CatchMapFooter } from './components/CatchMapFooter';
import { CatchMapHeader } from './components/CatchMapHeader';
import { CatchMapLayer } from './components/CatchMapLayer';
import {
  NO_POSITION_MESSAGE,
  OWNED_COMPETITIVE_MESSAGE,
  OWNED_PRIVATE_MESSAGE,
  PRIVATE_MESSAGE,
} from './constants';
import { FishingWater } from '../PostsViewer/types/FishingWater';

export interface ICatchMapProps {
  isPro: boolean;
  locationPrivacy?: CatchLocationPrivacyTypes;
  hasExactPosition: boolean;
  latitude?: number;
  longitude?: number;
  className?: string;
  source: EventMykissCtaButtonClicked['properties']['source'];
  onClick?: () => void;
  ownedByCurrentUser: boolean;
  waterName?: string;
  waterDetails?: FishingWater;
}

// eslint-disable-next-line react/display-name
export const CatchMap = memo(
  ({
    isPro,
    locationPrivacy,
    hasExactPosition,
    latitude,
    longitude,
    className = '',
    source,
    onClick = () => {},
    ownedByCurrentUser,
    waterName = '',
    waterDetails,
  }: ICatchMapProps) => {
    const isCatchMapVisible =
      hasExactPosition &&
      (ownedByCurrentUser || (isPro && locationPrivacy === CatchLocationPrivacyTypes.PUBLIC));

    const isCtaVisible =
      !isPro &&
      !ownedByCurrentUser &&
      hasExactPosition &&
      locationPrivacy === CatchLocationPrivacyTypes.PUBLIC;

    const waterMapIsVisible =
      !!waterDetails?._id &&
      !isCtaVisible &&
      (ownedByCurrentUser || locationPrivacy !== CatchLocationPrivacyTypes.PRIVATE);

    const getCatchMapMessage = (): string => {
      if (
        locationPrivacy === CatchLocationPrivacyTypes.PRIVATE &&
        !ownedByCurrentUser &&
        hasExactPosition
      ) {
        return PRIVATE_MESSAGE;
      }

      if (!hasExactPosition && !waterDetails?._id) {
        return NO_POSITION_MESSAGE;
      }
      return '';
    };

    const isCatchMapBodyVisible = isCtaVisible || !!getCatchMapMessage();
    const getCatMapFooterMessage = (): string => {
      if (!hasExactPosition) {
        return waterDetails?._id ? NO_POSITION_MESSAGE : '';
      }

      if (ownedByCurrentUser) {
        if (locationPrivacy === CatchLocationPrivacyTypes.PRIVATE) {
          return OWNED_PRIVATE_MESSAGE;
        }
        if (locationPrivacy === CatchLocationPrivacyTypes.COMPETITIVE) {
          return OWNED_COMPETITIVE_MESSAGE;
        }
      } else if (locationPrivacy === CatchLocationPrivacyTypes.COMPETITIVE) {
        return `This catch is logged in a secret spot at ${waterName}`;
      }
      return '';
    };

    return (
      <div className={`grid grid-cols-1 grid-rows-1 ${className}`}>
        <div className="col-start-1 row-start-1">
          <CatchMapLayer
            isCatchMapVisible={isCatchMapVisible}
            waterMapIsVisible={waterMapIsVisible}
            latitude={latitude}
            longitude={longitude}
            waterDetails={waterDetails}
          />
        </div>
        {(isCatchMapVisible || waterMapIsVisible) && <CatchMapHeader />}
        {isCatchMapBodyVisible ? (
          <CatchMapBody
            isCtaVisible={isCtaVisible}
            message={getCatchMapMessage()}
            onClick={onClick}
            isPrivate={hasExactPosition && locationPrivacy === CatchLocationPrivacyTypes.PRIVATE}
            source={source}
          />
        ) : (
          <CatchMapFooter
            waterName={waterName}
            message={getCatMapFooterMessage()}
            isPrivate={
              hasExactPosition &&
              (locationPrivacy === CatchLocationPrivacyTypes.PRIVATE ||
                locationPrivacy === CatchLocationPrivacyTypes.COMPETITIVE)
            }
          />
        )}
      </div>
    );
  },
);
